import React, { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import SEO from '../components/SEO'
import { getManualPage } from './GuideManualData'

function Lightbox({ src, alt, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
        <img src={src} alt={alt} className="w-full h-auto rounded-xl shadow-2xl" />
      </div>
    </div>
  )
}

function StepCard({ index, step, onPreview }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="p-5 sm:p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-dclaw-100 dark:bg-dclaw-900/30 text-dclaw-700 dark:text-dclaw-300 flex items-center justify-center font-bold">
            {index + 1}
          </div>
          <div className="flex-1">
            <p className="text-sm sm:text-base text-gray-700 dark:text-gray-200">{step.text}</p>
          </div>
        </div>
        {step.image && (
          <button type="button" onClick={() => onPreview(step.image, step.text)} className="mt-4 w-full">
            <img
              src={step.image}
              alt={step.text}
              className="w-full h-auto rounded-xl border border-gray-200 dark:border-gray-700"
              loading="lazy"
            />
          </button>
        )}
      </div>
    </div>
  )
}

function OverviewPage({ page }) {
  return (
    <div className="space-y-8">
      {page.summary && <p className="text-base text-gray-600 dark:text-gray-300">{page.summary}</p>}
      <div className="grid gap-6">
        {page.sections.map((s) => (
          <div
            key={s.title}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{s.title}</h3>
            <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-200">
              {s.bullets.map((b) => (
                <li key={b} className="flex gap-2">
                  <span className="mt-[6px] w-1.5 h-1.5 rounded-full bg-dclaw-500 flex-shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  )
}

function SecurityPage({ page, onPreview }) {
  const accentStyles = [
    {
      header: 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/40 dark:to-indigo-950/40',
      badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200',
      label: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-200'
    },
    {
      header: 'bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/40 dark:to-teal-950/40',
      badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200',
      label: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-200'
    },
    {
      header: 'bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/40',
      badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-200',
      label: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-200'
    }
  ]

  return (
    <div className="space-y-8">
      {page.image && (
        <button type="button" onClick={() => onPreview(page.image, page.title)} className="w-full">
          <img
            src={page.image}
            alt={page.title}
            className="w-full h-auto rounded-2xl border border-gray-200 dark:border-gray-700"
            loading="lazy"
          />
        </button>
      )}
      <div className="space-y-6">
        {(page.tables || []).map((t, tableIdx) => (
          (() => {
            const style = accentStyles[tableIdx % accentStyles.length]
            return (
          <div key={t.title} className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className={`px-6 py-4 border-b border-gray-100 dark:border-gray-700 ${style.header}`}>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t.title}</h3>
            </div>
            <div>
              <table className="w-full text-sm table-fixed">
                <colgroup>
                  <col className="w-[160px]" />
                  <col className="w-[320px]" />
                  <col />
                </colgroup>
                <thead className="bg-gray-50 dark:bg-gray-800/50">
                  <tr>
                    <th className="text-left font-semibold text-gray-900 dark:text-white px-4 py-3">要点</th>
                    <th className="text-left font-semibold text-gray-900 dark:text-white px-4 py-3">DClaw 做法</th>
                    <th className="text-left font-semibold text-gray-900 dark:text-white px-4 py-3">对比</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {(t.rows || []).map((r, idx) => (
                    <tr key={`${t.title}-${idx}`} className="align-top">
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ${style.badge}`}>
                          {r.point}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-gray-700 dark:text-gray-200 leading-snug break-words">{r.what}</td>
                      <td className="px-4 py-4 text-gray-700 dark:text-gray-200 leading-snug break-words">{r.compare}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
            )
          })()
        ))}
        {(!page.tables || page.tables.length === 0) && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden p-6 text-sm text-gray-600 dark:text-gray-300">
            暂无内容
          </div>
        )}
      </div>
    </div>
  )
}

export default function GuideManual() {
  const { slug } = useParams()
  const page = useMemo(() => getManualPage(slug), [slug])
  const [preview, setPreview] = useState(null)

  if (!page) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
        <SEO title="使用手册" description="DClaw 使用手册。" />
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">未找到页面</h2>
        <p className="text-gray-600 dark:text-gray-300 text-sm">请从左侧导航选择一个章节。</p>
      </div>
    )
  }

  return (
    <>
      <SEO title={page.title} description={`DClaw 使用手册：${page.title}`} />
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{page.title}</h2>
        {page.lead && <p className="text-gray-600 dark:text-gray-300 text-sm mb-6">{page.lead}</p>}

        {page.type === 'overview' && <OverviewPage page={page} />}
        {page.type === 'security' && <SecurityPage page={page} onPreview={(src, alt) => setPreview({ src, alt })} />}
        {page.type === 'steps' && (
          <div className="grid gap-6">
            {page.steps.map((step, idx) => (
              <StepCard key={`${idx}-${step.image || ''}`} index={idx} step={step} onPreview={(src, alt) => setPreview({ src, alt })} />
            ))}
          </div>
        )}
      </div>

      {preview && <Lightbox src={preview.src} alt={preview.alt} onClose={() => setPreview(null)} />}
    </>
  )
}
